import type { JSX } from 'react';

import type { LedgerKind } from './schemas';
import { rupees } from './schemas';
import { strings } from './strings';

/** Interpolate `{{name}}` placeholders. */
export function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_match, key: string) =>
    String(values[key] ?? `{{${key}}}`),
  );
}

const KIND_LABEL: Record<LedgerKind, string> = {
  topup: strings.kind_topup,
  bonus: strings.kind_bonus,
  redeem: strings.kind_redeem,
  refund: strings.kind_refund,
  expire: strings.kind_expire,
  adjust: strings.kind_adjust,
};

/** What one ledger kind is called on screen. */
export function kindLabel(kind: LedgerKind): string {
  return KIND_LABEL[kind];
}

/**
 * A signed amount, with its sign shown rather than inferred.
 *
 * `−₹500.00` and `₹500.00` are different events in somebody's financial
 * history, and a row that showed both as `₹500.00` with a colour would be
 * unreadable to a client printing their statement in black and white.
 */
export function Amount({ paise }: { paise: number }): JSX.Element {
  const sign = paise < 0 ? '−' : '';
  return (
    <span data-testid="amount" className={paise < 0 ? 'text-destructive' : undefined}>
      {sign}
      {rupees(Math.abs(paise))}
    </span>
  );
}
